import {
  applicationTypes,
  maxContractDemandForDomesticPremise,
  maxContractDemandForNonDomesticPremise,
  maxWaterDemand,
  regexForName,
} from "common/constant";
import { getOptionValue } from "common/enums";
import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";

import * as Yup from "yup";
import { getDocumentsValidationSchema } from "./getDocumentsValidationSchema";
import { proofOfOwnershipValidation } from "./proofOfOwnershipValidation";
import moment from "moment";

const applicantDetailsSchema = Yup.object({
  utilities: Yup.array().min(1, "Utility is required"),
  consumerNumber: Yup.string().test(
    "isConsumerNumberRequired",
    "",
    function () {
      const [{ value: current = null } = {}] = this.from || [];

      if (
        current.applicationTypeId ===
          applicationTypes["Name Change/Transfer"] &&
        !current.consumerNumber
      ) {
        return this.createError({
          message: "Consumer number is required",
        });
      }
      return true;
    }
  ),
  applicant: Yup.object({
    applicantTypeId: Yup.string().required("Applicant Type is required"),
    applicantName: Yup.string()
      .matches(regexForName, "Please enter a valid name")
      .required("Applicant Name is required"),
    panNumber: Yup.string()
      .matches(
        /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
        "Please enter a valid Pan number"
      )
      .test("isPanNumberRequired", "", function () {
        const [
          { value: current = null } = {},
          { value: { utilities, extraValues: { applicantType } } = {} } = {},
        ] = this.from || [];

        const isIndividualApplicantType =
          applicantType.find(
            (applicant) => applicant.name.toLowerCase() === "individual"
          )?.id === current.applicantTypeId;

        if (
          (!isIndividualApplicantType ||
            utilities.some(
              ({ value }) => value === getOptionValue("utilityOptions", "DCS")
            )) &&
          !current.panNumber
        ) {
          return this.createError({
            message: "Pan number is required",
          });
        }
        return true;
      }),
    isGSTRegistered: Yup.string().required("GST is required"),
    gstNumber: Yup.string()
      .test("isGstRequired", "", function (_, context) {
        const [{ value: { isGSTRegistered, gstNumber } } = {}] =
          context.from || [];
        if (isGSTRegistered && !gstNumber) {
          return context.createError({
            message: "GST number is required",
          });
        }
        return true;
      })
      .matches(
        /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
        "Please enter a valid GST number"
      ),
    occupancyTypeId: Yup.string().required("Occupancy type is required"),
    addressType: Yup.string().required("Address type is required"),
    city: Yup.object({
      id: Yup.number().required("City is required"),
    }),
    correspondenceAddress: Yup.string()
      .matches(/^[\w]+[\w\s.,-]*[\w\s.,-]+/, "Please enter valid address")
      .required("Correspondence Address is required"),
    pinCode: Yup.string()
      .matches(/^\d{6}$/, "Please enter valid pincode")
      .required("Pincode is required"),
    email: Yup.string()
      .email("Please enter valid email")
      .required("Email is required"),
    mobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid mobile number")
      .required("Mobile number is required"),
    alternateMobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid mobile number")
      .test("isValidAlternativeNumber", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        const isSameNumber =
          +current.mobileNumber === +current.alternateMobileNumber;
        if (isSameNumber) {
          return this.createError({
            message: "Alternate mobile number can't be same as mobile number.",
          });
        }
        return true;
      }),
    alternateEmail: Yup.array().test("isValidAlternateEmail", "", function () {
      const [{ value: current = null } = {}] = this.from || [];

      if (
        !!current.alternateEmail.length &&
        !current.alternateEmail.every((email) =>
          email?.match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          )
        )
      ) {
        return this.createError({
          message: "Enter valid emails",
        });
      }
      if (current.alternateEmail.some((email) => email === current.email)) {
        return this.createError({
          message: "Alternate email can't be same as email.",
        });
      }
      return true;
    }),

    landLine: Yup.string().matches(
      /^\d{8,11}$/,
      "Please enter a valid landline number"
    ),
  }),
});

const authorizedPersonSchema = Yup.object({
  authorizedPerson: Yup.object({
    salutation: Yup.string().required("Salutation is required"),
    firstName: Yup.string()
      .matches(regexForName, "Please enter a valid first name")
      .required("First name is required"),
    middleName: Yup.string().matches(
      regexForName,
      "Please enter a valid middle name"
    ),
    lastName: Yup.string().matches(
      regexForName,
      "Please enter a valid last name"
    ),
    // gender: Yup.string().required("Gender is required"),
    // ageCategory: Yup.string().required("Age category is required"),
    email: Yup.string()
      .email("Please enter valid email")
      .required("Email is required"),
    mobileNumber: Yup.string()
      .required("Mobile number is required")
      .matches(/^\d{10}$/, "Please enter valid mobile number"),
    landLine: Yup.string().matches(
      /^\d{8,11}$/,
      "Please enter a valid landline number"
    ),
    imageUrl: Yup.string().test(
      "imageRequired",
      "Please upload image",
      function () {
        const [{ value: current = null } = {}] = this.from || [];
        return !!(current?.image || current?.imageUrl);
      }
    ),
  }),
});

const utilityAddressSchema = ({ isPermanent }) =>
  Yup.object({
    utilityAddress: Yup.object({
      buildingIdentificationName: isPermanent
        ? Yup.string().required("Building identification name is required")
        : Yup.string().nullable(),
      premiseTypeId: Yup.string().required("Premise type is required"),
      premiseUsagePurpose: Yup.string().required(
        "Premise usage purpose is required"
      ),
      premisesAreaId: Yup.string().required("Premise Area is required"),
      dcsDomesticPremiseType: Yup.string().test(
        "isDcsDomesticPremiseTypeValid",
        "",
        function () {
          const [
            { value: current = null } = {},
            { value: { utilities, extraValues: { premiseTypes } } = {} } = {},
          ] = this.from || [];

          if (
            utilities.some(
              ({ value }) => value === getOptionValue("utilityOptions", "DCS")
            )
          ) {
            if (
              premiseTypes
                ?.find(({ id }) => id === current.premiseTypeId)
                ?.name.toLowerCase() === "domestic" &&
              !current.dcsDomesticPremiseType
            ) {
              return this.createError({
                message: "Dcs Connection is required",
              });
            }
            return true;
          }
          return true;
        }
      ),
      dcsIndividualPremiseType: Yup.string().test(
        "isDcsIndividualPremiseTypeValid",
        "",
        function () {
          const [{ value: current = null } = {}] = this.from || [];
          if (
            current.dcsDomesticPremiseType ===
              getOptionValue("dcsConnectionTypeEnum", "Individual Units") &&
            !current.dcsIndividualPremiseType
          ) {
            return this.createError({
              message: "Individual Units Type is required",
            });
          }

          return true;
        }
      ),
      zoneName: Yup.string().required("Zone is required"),
      blockName: Yup.string().required("Block is required"),
      buildingName: Yup.string().required("Building is required"),
      roadName: Yup.string().required("Road name is required"),
      // floors: Yup.array().min(1, "Floor is required"),
      otherZone: Yup.string().test("isOtherZone", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.zoneName === "other" && !current.otherZone) {
          return this.createError({
            message: "Zone is required",
          });
        }
        return true;
      }),
      otherBlock: Yup.string().test("isOtherBlock", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.blockName === "other" && !current.otherBlock) {
          return this.createError({
            message: "Block is required",
          });
        }
        return true;
      }),
      otherBuilding: Yup.string().test("isOtherBuilding", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.buildingName === "other" && !current.otherBuilding) {
          return this.createError({
            message: "Building is required",
          });
        }
        return true;
      }),

      otherRoad: Yup.string().test("isOtherRoad", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.roadName === "other" && !current.otherRoad) {
          return this.createError({
            message: "Road is required",
          });
        }
        return true;
      }),
      unitName: Yup.string().test("isUnitNameRequired", "", function () {
        const [
          { value: current = null } = {},
          { value: { utilities, extraValues: { premiseTypes } } = {} } = {},
        ] = this.from || [];

        const isOnlyDcsUtility = utilities.every(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        );

        const isNonDomesticPremise =
          premiseTypes
            ?.find((premiseType) => premiseType.id === current.premiseTypeId)
            ?.name?.toLowerCase() === "non-domestic";

        if (isOnlyDcsUtility && !current.unitName) {
          if (!isNonDomesticPremise) {
            return this.createError({
              message: "Unit ∕ Flat No is required",
            });
          }
          return true;
        } else if (!current.unitName) {
          return this.createError({
            message: "Unit ∕ Flat No is required",
          });
        }
        return true;
      }),
    }),
  });

const contractDemandSchema = Yup.object({
  totalBuildupArea: Yup.number()
    .positive("Total build up area of must be positive")
    .required("Total build up area is required")
    .test("isValidTotalBuildupArea", "", (_, context) => {
      const [{ value: { totalBuildupArea } = {} } = {}] = context.from || [];

      if (totalBuildupArea > 99999999) {
        return context.createError({
          message: "Maximum value of total build up area is 99999999",
        });
      }

      return true;
    }),
  totalNumberOfOccupants: Yup.number()
    .positive("Total number of must be positive")
    .required("Total number of occupants is required"),
  expectedConnectionStartDate: Yup.date().test(
    "minStartDate",
    "",
    (_, context) => {
      const [
        { value: { expectedConnectionStartDate, applicationTypeId } = {} } = {},
      ] = context.from || [];

      if (applicationTypeId === applicationTypes["Temporary Connection"]) {
        if (!expectedConnectionStartDate) {
          return context.createError({
            message: "Connection start date is required",
          });
        }
        if (
          expectedConnectionStartDate &&
          !expectedConnectionStartDate?.isValid()
        ) {
          return context.createError({
            message: "Enter a valid date",
          });
        }
      }

      return true;
    }
  ),
  expectedConnectionEndDate: Yup.date().test("minEndDate", "", (_, context) => {
    const [
      {
        value: {
          expectedConnectionEndDate,
          expectedConnectionStartDate,
          applicationTypeId,
        } = {},
      } = {},
    ] = context.from || [];

    if (applicationTypeId === applicationTypes["Temporary Connection"]) {
      if (!expectedConnectionEndDate) {
        return context.createError({
          message: "Connection end date is required",
        });
      }
      if (expectedConnectionEndDate && !expectedConnectionEndDate?.isValid()) {
        return context.createError({
          message: "Enter a valid date",
        });
      }
      if (
        expectedConnectionStartDate &&
        expectedConnectionEndDate.isSameOrBefore(expectedConnectionStartDate)
      ) {
        return context.createError({
          message: "Connection end date should be after connection start date",
        });
      }
    }

    return true;
  }),
  dcsDemand: Yup.object({
    contractDemand: Yup.number().test("isValidContractDemand", "", function () {
      const [
        { value: { contractDemand, ultimateDemand } = {} } = {},
        {
          value: { utilities, extraValues: { isNonDomesticPremise } } = {},
        } = {},
      ] = this.from || [];

      const keyName = isNonDomesticPremise ? "contract demand" : "demand load";

      if (
        utilities.some(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        )
      ) {
        if (!contractDemand) {
          return this.createError({
            message: `${keyName} is required`,
          });
        }
        if (contractDemand < 1) {
          return this.createError({
            message: `Enter valid ${keyName}`,
          });
        }
        if (contractDemand > 9999) {
          return this.createError({
            message: `Maximum value for ${keyName} is 9999`,
          });
        }
        if (contractDemand > ultimateDemand) {
          return this.createError({
            message: `Maximum value for ${keyName} is ${ultimateDemand}`,
          });
        }
      }

      return true;
    }),
    ultimateDemand: Yup.number().test("isValidUltimateDemand", "", function () {
      const [
        { value: { ultimateDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      // check for utilities here
      if (
        utilities.some(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        )
      ) {
        if (!ultimateDemand) {
          return this.createError({
            message: "Ultimate Demand is required",
          });
        }
        if (ultimateDemand < 1) {
          return this.createError({
            message: "Enter valid ultimate demand",
          });
        }
        if (ultimateDemand > 9999) {
          return this.createError({
            message: "Maximum value for ultimate demand is 9999",
          });
        }
      }
      return true;
    }),
    chilledWaterLineDiameter: Yup.number().test(
      "isValidChilledWaterLineDiameter ",
      "",
      function () {
        const [
          { value: { chilledWaterLineDiameter } = {} } = {},
          {
            value: {
              utilities,
              utilityAddress: { dcsDomesticPremiseType },
            } = {},
          } = {},
        ] = this.from || [];

        // check for utilities here
        if (
          utilities.some(
            ({ value }) => value === getOptionValue("utilityOptions", "DCS")
          ) &&
          dcsDomesticPremiseType ===
            getOptionValue("dcsConnectionTypeEnum", "Individual Units")
        ) {
          if (!chilledWaterLineDiameter) {
            return this.createError({
              message: "Chilled water line diameter is required",
            });
          }

          if (chilledWaterLineDiameter > 999) {
            return this.createError({
              message: "Maximum value for chilled water line diameter is 999",
            });
          }
        }
        return true;
      }
    ),
    waterFlowRate: Yup.number().test("isValidWaterFlowRate", "", function () {
      const [
        { value: { waterFlowRate } = {} } = {},
        {
          value: { utilities, utilityAddress: { dcsDomesticPremiseType } } = {},
        } = {},
      ] = this.from || [];

      // check for utilities here
      if (
        utilities.some(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        ) &&
        dcsDomesticPremiseType ===
          getOptionValue("dcsConnectionTypeEnum", "Individual Units")
      ) {
        if (!waterFlowRate) {
          return this.createError({
            message: "Water flow rate is required",
          });
        }

        if (waterFlowRate > 999) {
          return this.createError({
            message: "Maximum value for water flow rate is 999",
          });
        }
      }
      return true;
    }),
  }),

  waterSewageDemand: Yup.object({
    waterDemand: Yup.number().test("isValidWaterDemand", "", function () {
      const [
        { value: { waterDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!waterDemand) {
          return this.createError({
            message: "Total water demand is required",
          });
        }
        if (waterDemand > maxWaterDemand) {
          return this.createError({
            message: `Maximum value of total water demand is ${maxWaterDemand}`,
          });
        }
      }
      return true;
    }),
    flushing: Yup.number().test("isValidFlushing", "", function () {
      const [
        { value: { flushing, waterDemand, domestic, gardening } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      const totalOfFlushingDomesticAndGardeningDemand =
        flushing + domestic + gardening;

      // check for utilities here

      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!flushing && flushing !== 0) {
          return this.createError({
            message: "Flushing is required",
          });
        }
        if (flushing < 0) {
          return this.createError({
            message: "Enter valid flushing value",
          });
        }
        if (totalOfFlushingDomesticAndGardeningDemand > waterDemand) {
          return this.createError({
            message: `You have exceeded total water demand limit ${waterDemand} Liters/day`,
          });
        }
      }
      return true;
    }),
    domestic: Yup.number().test("isValidDomestic", "", function () {
      const [
        { value: { domestic, flushing, gardening, waterDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      const totalOfFlushingDomesticAndGardeningDemand =
        flushing + domestic + gardening;
      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!domestic && domestic !== 0) {
          return this.createError({
            message: "Domestic is required",
          });
        }
        if (domestic < 0) {
          return this.createError({
            message: "Enter valid domestic value",
          });
        }
        if (totalOfFlushingDomesticAndGardeningDemand > waterDemand) {
          return this.createError({
            message: `You have exceeded total water demand limit ${waterDemand} Liters/day`,
          });
        }
      }
      return true;
    }),
    gardening: Yup.number().test("isValidGardening", "", function () {
      const [
        { value: { gardening, flushing, domestic, waterDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];
      const totalOfFlushingDomesticAndGardeningDemand =
        flushing + domestic + gardening;
      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!gardening && gardening !== 0) {
          return this.createError({
            message: "Gardening is required",
          });
        }
        if (gardening < 0) {
          return this.createError({
            message: "Enter valid gardening value",
          });
        }

        if (totalOfFlushingDomesticAndGardeningDemand > waterDemand) {
          return this.createError({
            message: `You have exceeded total water demand limit ${waterDemand} Liters/day`,
          });
        }
      }
      return true;
    }),
  }),
});

const documentSchema = Yup.object({
  documents: getDocumentsValidationSchema(),
  proofOfOwnershipDescription: proofOfOwnershipValidation,
});

const documentSchemaForNameChangeTransfer = Yup.object({
  documents: getDocumentsValidationSchema(),
  parentId: Yup.string().required("Reason of name transfer is required"),
  proofOfOwnershipDescription: proofOfOwnershipValidation,
});

const declarationSchema = Yup.object({
  declaration: Yup.object({
    isConsumerDuesOutstanding: Yup.boolean().required(
      "Consumer dues outstanding is required"
    ),
    consumerDuesOutstanding: Yup.string().when("isConsumerDuesOutstanding", {
      is: true,
      then: (schema) => getConsumerNumberValidation(schema),
      otherwise: (schema) => schema,
    }),
    isPremisesDuesOutstanding: Yup.boolean().required(
      "Premise dues outstanding is required"
    ),
    premisesDuesOutstanding: Yup.string().when("isPremisesDuesOutstanding", {
      is: true,
      then: (schema) => getConsumerNumberValidation(schema),
      otherwise: (schema) => schema,
    }),
  }),
});

const demandExtensionSchema = Yup.object({
  utilityConsumerId: Yup.number().required("Customer number is required"),
  utilities: Yup.array().min(1, "Utility is required"),
  applicant: Yup.object({
    panNumber: Yup.string()
      .matches(
        /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
        "Please enter a valid Pan number"
      )
      .test("isPanNumberRequired", "", function () {
        const [
          { value: current = null } = {},
          { value: { utilities, extraValues: { applicantType } } = {} } = {},
        ] = this.from || [];

        const isIndividualApplicantType =
          applicantType.find(
            (applicant) => applicant.name.toLowerCase() === "individual"
          )?.id === current.applicantTypeId;
        if (
          (!isIndividualApplicantType ||
            utilities.some(
              ({ value }) => value === getOptionValue("utilityOptions", "DCS")
            )) &&
          !current.panNumber
        ) {
          return this.createError({
            message: "Pan number is required",
          });
        }
        return true;
      }),
    isGSTRegistered: Yup.string().required("GST is required"),
    gstNumber: Yup.string()
      .test("isGstRequired", "", function (_, context) {
        const [{ value: { isGSTRegistered, gstNumber } } = {}] =
          context.from || [];
        if (isGSTRegistered && !gstNumber) {
          return context.createError({
            message: "GST number is required",
          });
        }
        return true;
      })
      .matches(
        /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
        "Please enter a valid GST number"
      ),
  }),
  dcsDemand: Yup.object({
    increaseOfContractDemand: Yup.number().test(
      "isContractDemandValid",
      "",
      function () {
        const [
          {
            value: {
              increaseOfContractDemand,
              contractDemand,
              ultimateDemand,
            } = {},
          } = {},
          {
            value: { utilities, extraValues: { isNonDomesticPremise } } = {},
          } = {},
        ] = this.from || [];

        if (
          utilities.some(
            ({ value }) => value === getOptionValue("utilityOptions", "DCS")
          )
        ) {
          if (!increaseOfContractDemand) {
            if (
              (isNonDomesticPremise &&
                contractDemand === maxContractDemandForNonDomesticPremise) ||
              (!isNonDomesticPremise &&
                contractDemand === maxContractDemandForDomesticPremise)
            ) {
              return this.createError({
                message: `You have exceeded the limit of ${contractDemand}`,
              });
            } else {
              return this.createError({
                message: "Increase of Contract Demand is required",
              });
            }
          }
          if (increaseOfContractDemand < 0) {
            return this.createError({
              message: "Enter valid contract demand",
            });
          }
          if (
            isNonDomesticPremise &&
            increaseOfContractDemand + contractDemand >
              maxContractDemandForNonDomesticPremise
          ) {
            return this.createError({
              message: `Maximum value allowed for contract demand is ${
                maxContractDemandForNonDomesticPremise - contractDemand
              }`,
            });
          }

          if (
            !isNonDomesticPremise &&
            increaseOfContractDemand + contractDemand >
              maxContractDemandForDomesticPremise
          ) {
            return this.createError({
              message: `Maximum value allowed for contract demand is ${
                maxContractDemandForDomesticPremise - contractDemand
              }`,
            });
          }
          if (contractDemand + increaseOfContractDemand > ultimateDemand) {
            return this.createError({
              message: `Maximum value allowed for contract demand is ${ultimateDemand}`,
            });
          }
        }
        return true;
      }
    ),
  }),
  waterSewageDemand: Yup.object({
    increaseOfWaterDemand: Yup.number()
      .required("Increase of Water Demand is Required")
      .test("isValidIncreaseOfWaterDemand", "", function () {
        const [
          { value: { increaseOfWaterDemand, waterDemand } = {} } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];

        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (increaseOfWaterDemand < 0) {
            return this.createError({
              message: "Enter valid water demand",
            });
          }
          if (increaseOfWaterDemand + waterDemand > maxWaterDemand) {
            return this.createError({
              message: `Maximum value allowed for water is ${
                maxWaterDemand - waterDemand
              }`,
            });
          }
        }
        return true;
      }),
    increaseOfFlushing: Yup.number()
      .required("Increase of Flushing is Required")
      .test("isValidFlushing", "", function () {
        const [
          {
            value: {
              increaseOfFlushing,
              flushing,
              increaseOfWaterDemand,
              waterDemand,
              increaseOfDomestic,
              domestic,
              increaseOfGardening,
              gardening,
            } = {},
          } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];

        const totalWaterDemand = increaseOfWaterDemand + waterDemand;
        const totalOfFlushingDomesticAndGardeningDemand =
          increaseOfFlushing +
          flushing +
          increaseOfDomestic +
          domestic +
          increaseOfGardening +
          gardening;

        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (increaseOfFlushing < 0) {
            return this.createError({
              message: "Enter valid flushing demand",
            });
          }
          if (totalOfFlushingDomesticAndGardeningDemand > totalWaterDemand) {
            return this.createError({
              message: `You have exceeded total water demand limit ${totalWaterDemand} Liters/day`,
            });
          }
        }
        return true;
      }),
    increaseOfDomestic: Yup.number()
      .required("Increase of Domestic is Required")
      .test("isValidDomestic", "", function () {
        const [
          {
            value: {
              increaseOfDomestic,
              domestic,
              increaseOfFlushing,
              flushing,
              increaseOfGardening,
              gardening,
              increaseOfWaterDemand,
              waterDemand,
            } = {},
          } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];

        const totalWaterDemand = increaseOfWaterDemand + waterDemand;
        const totalOfFlushingDomesticAndGardeningDemand =
          increaseOfFlushing +
          flushing +
          increaseOfDomestic +
          domestic +
          increaseOfGardening +
          gardening;

        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (increaseOfDomestic < 0) {
            return this.createError({
              message: "Enter valid domestic demand",
            });
          }
          if (totalOfFlushingDomesticAndGardeningDemand > totalWaterDemand) {
            return this.createError({
              message: `You have exceeded total water demand limit ${totalWaterDemand} Liters/day`,
            });
          }
        }
        return true;
      }),
    increaseOfGardening: Yup.number()
      .required("Increase of Gardening is Required")
      .test("isValidGardening", "", function () {
        const [
          {
            value: {
              increaseOfDomestic,
              domestic,
              increaseOfFlushing,
              flushing,
              increaseOfGardening,
              gardening,
              increaseOfWaterDemand,
              waterDemand,
            } = {},
          } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];

        const totalWaterDemand = increaseOfWaterDemand + waterDemand;
        const totalOfFlushingDomesticAndGardeningDemand =
          increaseOfFlushing +
          flushing +
          increaseOfDomestic +
          domestic +
          increaseOfGardening +
          gardening;
        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (increaseOfGardening < 0) {
            return this.createError({
              message: "Enter valid gardening demand",
            });
          }
          if (totalOfFlushingDomesticAndGardeningDemand > totalWaterDemand) {
            return this.createError({
              message: `You have exceeded total water demand limit ${totalWaterDemand} Liters/day`,
            });
          }
        }
        return true;
      }),
  }),
});

const demandReductionSchema = Yup.object({
  utilityConsumerId: Yup.number().required("Customer number is required"),
  utilities: Yup.array().min(1, "Utility is required"),
  applicant: Yup.object({
    panNumber: Yup.string()
      .matches(
        /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
        "Please enter a valid Pan number"
      )
      .test("isPanNumberRequired", "", function () {
        const [
          { value: current = null } = {},
          { value: { utilities, extraValues: { applicantType } } = {} } = {},
        ] = this.from || [];

        const isIndividualApplicantType =
          applicantType.find(
            (applicant) => applicant.name.toLowerCase() === "individual"
          )?.id === current.applicantTypeId;
        if (
          (!isIndividualApplicantType ||
            utilities.some(
              ({ value }) => value === getOptionValue("utilityOptions", "DCS")
            )) &&
          !current.panNumber
        ) {
          return this.createError({
            message: "Pan number is required",
          });
        }
        return true;
      }),
    isGSTRegistered: Yup.string().required("GST is required"),
    gstNumber: Yup.string()
      .test("isGstRequired", "", function (_, context) {
        const [{ value: { isGSTRegistered, gstNumber } } = {}] =
          context.from || [];
        if (isGSTRegistered && !gstNumber) {
          return context.createError({
            message: "GST number is required",
          });
        }
        return true;
      })
      .matches(
        /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
        "Please enter a valid GST number"
      ),
  }),
  dcsDemand: Yup.object({
    decreaseOfContractDemand: Yup.number().test(
      "isContractDemandValid",
      "",
      function () {
        const [
          { value: { contractDemand, decreaseOfContractDemand } = {} } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];

        if (
          utilities.some(
            ({ value }) => value === getOptionValue("utilityOptions", "DCS")
          )
        ) {
          if (!decreaseOfContractDemand) {
            return this.createError({
              message: "Decrease of Contract Demand is required",
            });
          }
          if (decreaseOfContractDemand < 0) {
            return this.createError({
              message: "Enter valid contract demand",
            });
          }

          if (decreaseOfContractDemand >= contractDemand) {
            return this.createError({
              message: "Value Should Be Lesser Than Existing Contract Demand",
            });
          }
        }
        return true;
      }
    ),
  }),
  waterSewageDemand: Yup.object({
    decreaseOfWaterDemand: Yup.number()
      .required("Decrease of Water Demand is Required")
      .test("isValidDecreaseOfWaterDemand", "", function () {
        const [
          { value: { decreaseOfWaterDemand, waterDemand } = {} } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];

        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (decreaseOfWaterDemand < 0) {
            return this.createError({
              message: "Enter valid flushing demand",
            });
          }
          if (decreaseOfWaterDemand >= waterDemand) {
            return this.createError({
              message: "Value Should Be Lesser Than Existing Water Demand",
            });
          }
        }
        return true;
      }),
    decreaseOfFlushing: Yup.number()
      .required("Decrease of Flushing is Required")
      .test("isValidFlushing", "", function () {
        const [
          {
            value: {
              decreaseOfFlushing,
              flushing,
              waterDemand,
              decreaseOfWaterDemand,
              decreaseOfDomestic,
              domestic,
              gardening,
              decreaseOfGardening,
            } = {},
          } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];

        const totalWaterDemand = waterDemand - decreaseOfWaterDemand;
        const totalOfFlushingDomesticAndGardeningDemand =
          flushing -
          decreaseOfFlushing +
          (domestic - decreaseOfDomestic) +
          +(gardening - decreaseOfGardening);
        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (decreaseOfFlushing < 0) {
            return this.createError({
              message: "Enter valid flushing demand",
            });
          }
          if (decreaseOfFlushing > flushing) {
            return this.createError({
              message: "Value Should Be Lesser Than Existing Flushing Demand",
            });
          }
          if (totalOfFlushingDomesticAndGardeningDemand > totalWaterDemand) {
            return this.createError({
              message: `You have exceeded limit of ${totalWaterDemand} Liters/day`,
            });
          }
        }
        return true;
      }),
    decreaseOfDomestic: Yup.number()
      .required("Decrease of Domestic is Required")
      .test("isValidDomestic", "", function () {
        const [
          {
            value: {
              decreaseOfFlushing,
              flushing,
              waterDemand,
              decreaseOfWaterDemand,
              decreaseOfDomestic,
              domestic,
              gardening,
              decreaseOfGardening,
            } = {},
          } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];
        const totalWaterDemand = waterDemand - decreaseOfWaterDemand;
        const totalOfFlushingDomesticAndGardeningDemand =
          flushing -
          decreaseOfFlushing +
          (domestic - decreaseOfDomestic) +
          +(gardening - decreaseOfGardening);

        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (decreaseOfDomestic < 0) {
            return this.createError({
              message: "Enter valid domestic demand",
            });
          }
          if (decreaseOfDomestic > domestic) {
            return this.createError({
              message: "Value Should Be Lesser Than Existing Domestic Demand",
            });
          }
          if (totalOfFlushingDomesticAndGardeningDemand > totalWaterDemand) {
            return this.createError({
              message: `You have exceeded limit of ${totalWaterDemand} Liters/day`,
            });
          }
        }
        return true;
      }),
    decreaseOfGardening: Yup.number()
      .required("Decrease of Gardening is Required")
      .test("isValidGardening", "", function () {
        const [
          {
            value: {
              decreaseOfFlushing,
              flushing,
              waterDemand,
              decreaseOfWaterDemand,
              decreaseOfDomestic,
              domestic,
              gardening,
              decreaseOfGardening,
            } = {},
          } = {},
          { value: { utilities } = {} } = {},
        ] = this.from || [];
        const totalWaterDemand = waterDemand - decreaseOfWaterDemand;
        const totalOfFlushingDomesticAndGardeningDemand =
          flushing -
          decreaseOfFlushing +
          (domestic - decreaseOfDomestic) +
          +(gardening - decreaseOfGardening);
        if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          )
        ) {
          if (decreaseOfGardening < 0) {
            return this.createError({
              message: "Enter valid gardening demand",
            });
          }
          if (decreaseOfGardening > gardening) {
            return this.createError({
              message: "Value Should Be Lesser Than Existing Gardening Demand",
            });
          }

          if (totalOfFlushingDomesticAndGardeningDemand > totalWaterDemand) {
            return this.createError({
              message: `You have exceeded limit of ${totalWaterDemand} Liters/day`,
            });
          }
        }
        return true;
      }),
  }),
});

const RemovalOfConnectionSchema = Yup.object({
  utilityConsumerId: Yup.number().required("Customer number is required"),
  utilities: Yup.array().min(1, "Utility is required"),
  disconnectionDate: Yup.date()
    .required("Disconnection Date is required")
    .test("isValidDisconnectionDate", "", (_, context) => {
      const [{ value: { disconnectionDate } = {} } = {}] = context.from || [];
      if (disconnectionDate?.isSameOrBefore(moment())) {
        return context.createError({
          message: "Disconnection date must be in future",
        });
      }
      return true;
    }),
  applicant: Yup.object({
    panNumber: Yup.string()
      .matches(
        /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
        "Please enter a valid Pan number"
      )
      .test("isPanNumberRequired", "", function () {
        const [
          { value: current = null } = {},
          { value: { utilities, extraValues: { applicantType } } = {} } = {},
        ] = this.from || [];
        const isIndividualApplicantType =
          applicantType.find(
            (applicant) => applicant.name.toLowerCase() === "individual"
          )?.id === current.applicantTypeId;
        if (
          (!isIndividualApplicantType ||
            utilities.some(
              ({ value }) => value === getOptionValue("utilityOptions", "DCS")
            )) &&
          !current.panNumber
        ) {
          return this.createError({
            message: "Pan number is required",
          });
        }
        return true;
      }),
    isGSTRegistered: Yup.string().required("GST is required"),
    gstNumber: Yup.string()
      .test("isGstRequired", "", function (_, context) {
        const [{ value: { isGSTRegistered, gstNumber } } = {}] =
          context.from || [];
        if (isGSTRegistered && !gstNumber) {
          return context.createError({
            message: "GST number is required",
          });
        }
        return true;
      })
      .matches(
        /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
        "Please enter a valid GST number"
      ),
  }),
});

export const getConnectionSchema = ({ isPermanent }) => ({
  0: applicantDetailsSchema,
  1: authorizedPersonSchema,
  2: utilityAddressSchema({ isPermanent }),
  3: contractDemandSchema,
  4: documentSchema,
  5: declarationSchema,
});

export const getNameChangeTransferSchema = {
  0: applicantDetailsSchema,
  1: authorizedPersonSchema,
  2: documentSchemaForNameChangeTransfer,
  3: declarationSchema,
};

export const getDemandExtensionSchema = {
  0: demandExtensionSchema,
  1: authorizedPersonSchema,
  2: documentSchema,
  3: declarationSchema,
};

export const getDemandReductionSchema = {
  0: demandReductionSchema,
  1: authorizedPersonSchema,
  2: documentSchema,
  3: declarationSchema,
};

export const getRemovalOfConnectionSchema = {
  0: RemovalOfConnectionSchema,
  1: authorizedPersonSchema,
  2: documentSchema,
  3: declarationSchema,
};
